import React, { Fragment, useState } from "react";
import CommonDialog from "../../common/components/CommonDialog";
import { Box, Divider, Grid, Typography, useTheme } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import CommonCard from "../../common/components/CommonCard";
import CommonFormField from "../../common/components/CommonFormField";
import { ExamType, TestSources } from "../../common/const";
import CommonSelect from "../../common/components/CommonSelect";
import { Close } from "@mui/icons-material";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { createTestGoal, saveTestScore } from "../../api/satactApi";

const ScoreDialog = ({
    addOpen,
    setAddOpen,
    isDreamScore,
    getScoreData
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        examdate: '',
        testsource: TestSources[0].value,
        maths: '',
        english: '',
        reading: '',
        science: '',
        type: ExamType.SAT,
        notes: ''
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'examdate':
                if (!value && !isDreamScore) return 'Test Date is required';
                return '';
            case 'maths':
                if (!value) return 'Maths Score is required';
                return '';
            case 'english':
                if (!value) return 'English Score is required';
                return '';
            case 'reading':
                if (!value && formData.type === ExamType.ACT) return 'Reading Score is required';
                return '';
            case 'science':
                if (!value && formData.type === ExamType.ACT) return 'Science Score is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'english') {
            const englishValue = parseInt(value) || 0;
            if (formData.type === ExamType.SAT) {
                if (englishValue > 800 || englishValue < 0) {
                    showNotification('English score must be between 1 and 800', 'error');
                    return;
                }
            }
            else if (formData.type === ExamType.ACT) {
                if (englishValue > 36 || englishValue < 0) {
                    showNotification('English score must be between 0 and 36', 'error');
                    return;
                }
            }
        }
        if (name === 'maths') {
            const englishValue = parseInt(value) || 0;
            if (formData.type === ExamType.SAT) {
                if (englishValue > 800 || englishValue < 0) {
                    showNotification('Maths score must be between 0 and 800', 'error');
                    return;
                }
            }
            else if (formData.type === ExamType.ACT) {
                if (englishValue > 36 || englishValue < 0) {
                    showNotification('Maths score must be between 1 and 36', 'error');
                    return;
                }
            }
        }
        if (name === 'reading') {
            const englishValue = parseInt(value) || 0;
            if (formData.type === ExamType.ACT) {
                if (englishValue > 36 || englishValue < 0) {
                    showNotification('Reading score must be between 1 and 36', 'error');
                    return;
                }
            }
        }
        if (name === 'science') {
            const englishValue = parseInt(value) || 0;
            if (formData.type === ExamType.ACT) {
                if (englishValue > 36 || englishValue < 0) {
                    showNotification('Science score must be between 1 and 36', 'error');
                    return;
                }
            }
        }
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            let response;
            if (isDreamScore) {
                const data = {
                    "test_type": formData.type,
                    "student_id": user?.student_id,
                    "target_composite": formData.type === ExamType.SAT ? Number(formData.maths) + Number(formData.english) : ((Number(formData.maths) + Number(formData.english) + Number(formData.reading) + Number(formData.science)) / 4),
                    "target_sections": {
                        "Math": formData.maths,
                        "English": formData.english,
                        "Reading": formData.reading,
                        "Science": formData.science
                    }
                }
                response = await createTestGoal(data);
            } else {
                const data = {
                    "test_date": new Date(formData.examdate).toISOString(),
                    "source": formData.testsource,
                    "test_type": formData.type,
                    "student_id": user?.student_id,
                    "notes": formData.notes,
                    "sections": [
                        {
                            "section": 'Math',
                            "score": formData.maths,
                            "test_date": new Date(formData.examdate).toISOString()
                        },
                        {
                            "section": 'English',
                            "score": formData.english,
                            "test_date": new Date(formData.examdate).toISOString()
                        }
                    ]
                }
                if (formData.type === ExamType.ACT) {
                    data.sections.push({
                        "section": 'Reading',
                        "score": formData.reading,
                        "test_date": new Date(formData.examdate).toISOString()
                    },
                        {
                            "section": 'Science',
                            "score": formData.science,
                            "test_date": new Date(formData.examdate).toISOString()
                        });
                }
                response = await saveTestScore(data);
            }
            if (response.success) {
                setFormData({
                    examdate: '',
                    testsource: TestSources[0].value,
                    maths: '',
                    english: '',
                    reading: '',
                    science: '',
                    type: ExamType.SAT,
                    notes: ''
                });
                showNotification(response.message, 'success');
                setAddOpen(false);
                if (getScoreData) {
                    getScoreData();
                }
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <CommonDialog
            open={addOpen}
            size="xs"
            title={
                <Fragment>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{isDreamScore ? "Log Dream Score" : "Add New Score"}</Typography>
                        <Close
                            sx={{
                                color: theme.palette.background.dark, fontSize: 20,
                                float: 'right',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                setAddOpen(false);
                                setFormData({
                                    examdate: '',
                                    testsource: '',
                                    math: '',
                                    language: '',
                                    reading: '',
                                    science: '',
                                    type: ExamType.SAT,
                                    notes: ''
                                });
                            }}
                        />
                    </Box>
                    <Box width="80%" mb={0.5}>
                        <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Enter details from your latest practice test</Typography>
                    </Box>
                    <Divider />
                </Fragment>
            }
            content={
                <Grid container spacing={1.5} mb={2}>
                    {loading && (
                        <LoadingSpinner />
                    )}
                    <Grid item md={12}>
                        <Box mt={2} display="flex">
                            <CommonButton
                                label={ExamType.SAT}
                                width="100px"
                                fontWeight={600}
                                color={formData.type === ExamType.SAT ? theme.palette.secondary.main : 'gray'}
                                background={formData.type === ExamType.SAT ? theme.palette.primary.main : '#FAFAFA'}
                                handleClick={() => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        type: ExamType.SAT,
                                        english: '',
                                        maths: '',
                                        reading: '',
                                        science: '',
                                    }));
                                    setErrors({});
                                }}
                                marginBottom="0px"
                            />
                            <CommonButton
                                label={ExamType.ACT}
                                width="100px"
                                fontWeight={600}
                                color={formData.type === ExamType.ACT ? theme.palette.secondary.main : 'gray'}
                                background={formData.type === ExamType.ACT ? theme.palette.primary.main : '#FAFAFA'}
                                handleClick={() => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        type: ExamType.ACT,
                                        english: '',
                                        maths: '',
                                        reading: '',
                                        science: '',
                                    }));
                                    setErrors({});
                                }}
                                marginBottom="0px"
                            />
                        </Box>
                    </Grid>
                    {!isDreamScore && (
                        <>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Test Date"
                                    type="date"
                                    name="examdate"
                                    value={formData.examdate}
                                    onChange={handleInputChange}
                                    error={errors.examdate}
                                    placeholder="Select test date"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonSelect
                                    label="Test Source"
                                    name="testsource"
                                    value={formData.testsource}
                                    onChange={handleInputChange}
                                    placeholder="Select Source"
                                    options={TestSources}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item md={12}>
                        <CommonCard
                            borderRadius={1}
                            border={`1px solid ${theme.palette.cardbackground.main}`}
                            background={theme.palette.cardbackground.secondary}
                            padding="10px"
                            paddingBottom="0px"
                            marginTop="8px"
                            content={
                                <Fragment>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Typography fontWeight={600}>Selection Scores (Scale 1 - {formData.type === ExamType.SAT ? '800' : '36'})</Typography>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item md={6}>
                                            <CommonFormField
                                                label="English"
                                                type="number"
                                                name="english"
                                                value={formData.english}
                                                onChange={handleInputChange}
                                                error={errors.english}
                                                placeholder="Enter score"
                                            />
                                        </Grid>
                                        <Grid item md={6}>
                                            <CommonFormField
                                                label="Maths"
                                                type="number"
                                                name="maths"
                                                value={formData.maths}
                                                onChange={handleInputChange}
                                                error={errors.maths}
                                                placeholder="Enter score"
                                            />
                                        </Grid>
                                        {formData.type === ExamType.ACT && (
                                            <>
                                                <Grid item md={6}>
                                                    <CommonFormField
                                                        label="Reading"
                                                        type="number"
                                                        name="reading"
                                                        value={formData.reading}
                                                        onChange={handleInputChange}
                                                        error={errors.reading}
                                                        placeholder="Enter score"
                                                    />
                                                </Grid>
                                                <Grid item md={6}>
                                                    <CommonFormField
                                                        label="Science"
                                                        type="number"
                                                        name="science"
                                                        value={formData.science}
                                                        onChange={handleInputChange}
                                                        error={errors.science}
                                                        placeholder="Enter score"
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Fragment>
                            }
                        />
                    </Grid>
                    <Grid item md={12}>
                        <CommonCard
                            borderRadius={1}
                            background={theme.palette.background.main}
                            padding="10px"
                            paddingBottom="0px"
                            marginTop="8px"
                            content={
                                <Fragment>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Box>
                                            <Typography fontWeight={600}>Calculate Composite</Typography>
                                            <Typography fontSize={13} color={theme.palette.background.dark}>{formData.type === ExamType.SAT ? 'Sum of all two sections score' : 'Average of all four sections score'}</Typography>
                                        </Box>
                                        <Typography fontWeight={600} fontSize={20}>{formData.type === ExamType.SAT ?
                                            (Number(formData.english) + Number(formData.maths) || 0) :
                                            (((Number(formData.english) + Number(formData.maths) + Number(formData.reading) + Number(formData.science)) / 4) || 0)}
                                            <span style={{ fontSize: 16, color: theme.palette.background.dark }}>{formData.type === ExamType.SAT ? ' / 1600' : ' / 36'}</span>
                                        </Typography>
                                    </Box>
                                </Fragment>
                            }
                        />
                    </Grid>
                    {!isDreamScore && (
                        <Grid item md={12}>
                            <CommonFormField
                                label="Notes"
                                type="email"
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                placeholder="Add any observations or feedback about this test."
                                multiline
                            />
                        </Grid>
                    )}
                </Grid>
            }
            actionButtons={(
                <CommonButton
                    label={isDreamScore ? "Add Score" : "Save Score"}
                    size="small"
                    background={theme.palette.secondary.main}
                    color={theme.palette.background.main}
                    fontWeight={700}
                    marginBottom="10px"
                    handleClick={() => {
                        handleSubmit();
                    }}
                />
            )}
        />
    )
}

export default ScoreDialog;